import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

import { API_BASE_URL } from '../../config/api';

function FacilityView({ id, onNavigate }) {
  const { token } = useAuth();
  const [facility, setFacility] = useState(null);
  const [journeyName, setJourneyName] = useState('N/A');
  const [orgName, setOrgName] = useState('Independent');
  const [waypointName, setWaypointName] = useState('N/A');
  const [routeName, setRouteName] = useState('N/A');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFacilityDetails = async () => {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };
      try {
        const res = await fetch(`${API_BASE_URL}/facilities/${id}`, { headers });
        if (!res.ok) {
          setError('Failed to fetch facility details');
          setLoading(false);
          return;
        }

        const data = await res.json();
        setFacility(data);

        // Fetch associated names
        if (data.journey_id) {
          const jRes = await fetch(`${API_BASE_URL}/journeys/${data.journey_id}`, { headers });
          if (jRes.ok) {
            const jData = await jRes.json();
            setJourneyName(jData.name);
          }
        }

        if (data.organization_id) {
          const oRes = await fetch(`${API_BASE_URL}/organizations/`, { headers });
          if (oRes.ok) {
            const orgs = await oRes.json();
            const o = orgs.find(org => org.id === data.organization_id);
            if (o) setOrgName(o.name);
          }
        }

        if (data.waypoint_id) {
          // Fetch routes to find the waypoint's route and name
          const rRes = await fetch(`${API_BASE_URL}/routes`, { headers });
          if (rRes.ok) {
            const routes = await rRes.json();
            for (const r of routes) {
              const wRes = await fetch(`${API_BASE_URL}/routes/${r.id}`, { headers });
              if (wRes.ok) {
                const rData = await wRes.json();
                const wp = (rData.waypoints || []).find(w => w.id === data.waypoint_id);
                if (wp) {
                  setWaypointName(wp.waypoint_name || `Sequence ${wp.sequence_no}`);
                  setRouteName(r.name);
                  break;
                }
              }
            }
          }
        }

      } catch (e) {
        console.error('Error fetching facility details', e);
        setError('Network error. Failed to load facility details.');
      } finally {
        setLoading(false);
      }
    };

    fetchFacilityDetails();
  }, [id, token]);

  if (loading) {
    return <div className="card"><p>Loading facility details...</p></div>;
  }

  if (error || !facility) {
    return (
      <div className="card">
        <div style={{ color: '#f44336', marginBottom: '20px' }}>{error || 'Facility not found'}</div>
        <button className="btn-primary" onClick={() => onNavigate('facilities')}>Back to Directory</button>
      </div>
    );
  }

  const getBadgeClass = (type) => {
    switch (type) {
      case 'medical': return 'badge-danger';
      case 'accommodation': return 'badge-info';
      case 'food': return 'badge-success';
      default: return 'badge-info';
    }
  };

  return (
    <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid #eee', paddingBottom: '16px' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#212121', marginBottom: '6px' }}>{facility.name}</h2>
          <span className={getBadgeClass(facility.facility_type)} style={{ fontSize: '14px', padding: '6px 12px' }}>
            {facility.facility_type.toUpperCase()}
          </span>
        </div>
        <button className="btn-primary" onClick={() => onNavigate('facilities')}>Back to Directory</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
        <div style={{ background: '#fcfcfc', padding: '16px', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
          <h4 style={{ color: '#FF5722', marginBottom: '12px', fontSize: '16px' }}>Operational Info</h4>
          <p style={{ marginBottom: '8px' }}><strong>Status:</strong> {facility.verified ? 'Verified (Official)' : 'Pending Verification'}</p>
          <p style={{ marginBottom: '8px' }}><strong>Contact:</strong> {facility.contact_number || 'N/A'}</p>
          <p style={{ marginBottom: '8px' }}><strong>Partner Org:</strong> {orgName}</p>
        </div>

        <div style={{ background: '#fcfcfc', padding: '16px', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
          <h4 style={{ color: '#FF5722', marginBottom: '12px', fontSize: '16px' }}>Geospatial Mapping</h4>
          <p style={{ marginBottom: '8px' }}><strong>Latitude:</strong> {facility.latitude}</p>
          <p style={{ marginBottom: '8px' }}><strong>Longitude:</strong> {facility.longitude}</p>
          <p style={{ marginBottom: '8px' }}><strong>Journey Season:</strong> {journeyName}</p>
          <p style={{ marginBottom: '8px' }}><strong>Route:</strong> {routeName}</p>
          <p style={{ marginBottom: '8px' }}><strong>Route Point (Waypoint):</strong> {waypointName}</p>
        </div>
      </div>

      <div style={{ background: '#fcfcfc', padding: '20px', borderRadius: '8px', border: '1px solid #f0f0f0', marginBottom: '24px' }}>
        <h4 style={{ color: '#FF5722', marginBottom: '12px', fontSize: '16px' }}>Description / Notes</h4>
        <p style={{ lineHeight: '1.6', color: '#555', whiteSpace: 'pre-wrap' }}>
          {facility.description || 'No description provided for this facility.'}
        </p>
      </div>
    </div>
  );
}

export default FacilityView;
